import { formatCurrency } from './currency';
import { formatDate } from './dates';
import { buildInvoicePdfBlob, downloadInvoicePdf } from './invoicePdf';

/** Digits only for wa.me (include country code, no + or spaces). */
export function normalizeWhatsAppPhone(phone) {
  let digits = String(phone || '').replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  return digits;
}

export function buildInvoiceWhatsAppText(invoice, customer) {
  const name = customer?.name || invoice?.customer_name || 'Customer';
  const store = invoice?.client_name || 'Store';
  const lines = [
    `*Invoice ${invoice?.invoice_number || ''}*`,
    `From: ${store}`,
    `To: ${name}`,
    `Date: ${formatDate(invoice?.issue_date)}`,
    `Status: ${invoice?.status || '-'}`,
    ''
  ];

  const items = invoice?.items || [];
  if (items.length) {
    lines.push('*Items*');
    items.forEach((item, i) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unit_price) || 0;
      const disc = Number(item.discount) || 0;
      const lineTotal = qty * price - disc;
      if (disc > 0) {
        lines.push(
          `${i + 1}. ${item.description || 'Item'} × ${qty} @ ${formatCurrency(price)} − disc ${formatCurrency(disc)} = ${formatCurrency(lineTotal)}`
        );
      } else {
        lines.push(`${i + 1}. ${item.description || 'Item'} × ${qty} = ${formatCurrency(lineTotal)}`);
      }
    });
    lines.push('');
  }

  lines.push(`Subtotal: ${formatCurrency(invoice?.subtotal || 0)}`);
  if (Number(invoice?.tax_amount) > 0) {
    lines.push(`GST: ${formatCurrency(invoice.tax_amount)}`);
  }
  if (Number(invoice?.discount) > 0) {
    lines.push(`Discount: -${formatCurrency(invoice.discount)}`);
  }
  lines.push(`*Total: ${formatCurrency(invoice?.total_amount || 0)}*`);
  lines.push('');
  lines.push('Thank you for your business!');

  return lines.join('\n');
}

function openWhatsAppText(invoice, customer, extraNote = '') {
  const text = `${buildInvoiceWhatsAppText(invoice, customer)}${extraNote ? `\n\n${extraNote}` : ''}`;
  const existing =
    customer?.phone ||
    invoice.customer_phone ||
    '';

  const entered = window.prompt(
    'WhatsApp number with country code (digits only).\nExample: 919876543210 or 9659xxxxxxx\nLeave blank to choose contact in WhatsApp:',
    existing
  );

  if (entered === null) return false;

  const digits = normalizeWhatsAppPhone(entered);
  const encoded = encodeURIComponent(text);
  const url = digits.length >= 8
    ? `https://wa.me/${digits}?text=${encoded}`
    : `https://wa.me/?text=${encoded}`;

  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}

/**
 * Free text-only WhatsApp share via wa.me (no Business API).
 */
export function sendInvoiceOnWhatsApp(invoice, customer) {
  if (!invoice) return false;
  return openWhatsAppText(invoice, customer);
}

/**
 * Free PDF share: uses the system share sheet (pick WhatsApp) when the browser
 * supports file sharing; otherwise downloads the PDF and opens WhatsApp text.
 */
export async function shareInvoicePdfOnWhatsApp(invoice, customer) {
  if (!invoice) return { ok: false, mode: 'none' };

  const text = buildInvoiceWhatsAppText(invoice, customer);
  const { blob, fileName } = buildInvoicePdfBlob(invoice, customer);
  const file = new File([blob], fileName, { type: 'application/pdf' });

  const canShareFiles =
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function' &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ files: [file] });

  if (canShareFiles) {
    try {
      await navigator.share({
        files: [file],
        title: `Invoice ${invoice.invoice_number || ''}`,
        text
      });
      return { ok: true, mode: 'share' };
    } catch (err) {
      if (err && err.name === 'AbortError') {
        return { ok: false, mode: 'cancelled' };
      }
      // Fall through to download + wa.me
    }
  }

  await downloadInvoicePdf(invoice, customer);
  const opened = openWhatsAppText(
    invoice,
    customer,
    '(PDF downloaded — please attach the invoice PDF in WhatsApp.)'
  );
  return { ok: opened, mode: 'download+text' };
}
